import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useSubmitPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useSubmitPayeeInfo';
import {
  RECONCILIATION_DEFAULT_STATUS,
  RECONCILIATION_STATUS_CAPTION_KEYS,
  RECONCILIATION_STATUS_LABEL_KEYS,
  RECONCILIATION_STATUS_ORDER,
  ReconciliationSettingData,
  ReconciliationSettingForm,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  FormRadioButton,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';

const cx = classnames.bind(styles);

export type Props = {
  data?: ReconciliationSettingData | undefined;
};

export const Edit: FC<Props> = memo(({ data }) => {
  const { onEditFormDirtyChange, handleCancelEdit } = usePayeeDetailDrawer();
  const { t } = useTranslation();

  const defaultValues: ReconciliationSettingForm = useMemo(
    () => ({
      ap_payee_reconciliation_setting: {
        // Null status means the payee has no row yet — the BE column default applies.
        status:
          data?.ap_payee_reconciliation_setting?.status ??
          RECONCILIATION_DEFAULT_STATUS,
      },
    }),
    [data?.ap_payee_reconciliation_setting?.status]
  );

  const methods = useForm<ReconciliationSettingForm>({
    defaultValues,
    mode: 'all',
  });

  const { isValid, isDirty, isSubmitting } = useFormState({
    control: methods.control,
  });

  const { onSubmit, isLoading } = useSubmitPayeeInfo<ReconciliationSettingForm>(
    {
      apPayeeId: data?.id ?? '',
    }
  );

  useEffect(() => {
    if (!isSubmitting) return;
    onEditFormDirtyChange?.(false);
  }, [isSubmitting, onEditFormDirtyChange]);

  useEffect(() => {
    onEditFormDirtyChange?.(isDirty);
  }, [isDirty, onEditFormDirtyChange]);

  const handleCancel = useCallback(() => {
    handleCancelEdit?.(isDirty);
  }, [handleCancelEdit, isDirty]);

  const statusOptions = useMemo(
    () =>
      RECONCILIATION_STATUS_ORDER.map((status) => ({
        id: `reconciliation-status-${status}`,
        label: t(RECONCILIATION_STATUS_LABEL_KEYS[status]),
        value: status,
        caption: {
          type: 'helper' as const,
          icon: false,
          textClassName: cx('statusCaption'),
          children: t(RECONCILIATION_STATUS_CAPTION_KEYS[status]),
        },
      })),
    [t]
  );

  return (
    <Form<ReconciliationSettingForm>
      formMethod={methods}
      onSubmit={onSubmit}
      testId='reconciliation-setting-edit'
      className={cx('sectionContainer')}
    >
      <div className={cx('reconciliationHeaderRow')}>
        <div className={cx('reconciliationHeaderContent')}>
          <HeaderSection displayName={t('payee_reconciliation_setting')} />
          <div className={cx('sectionDescription')}>
            {t('payee_reconciliation_setting_description')}
          </div>
        </div>
        <ButtonGroup direction='horizontal'>
          <ButtonV2
            color='primary'
            isSecondary
            onClick={handleCancel}
            type='button'
            isDisabled={isLoading}
            testId='cancel-button'
            containerClassName={cx('cancelButton')}
          >
            {t('cancel')}
          </ButtonV2>
          <ButtonV2
            color='primary'
            size='xs'
            type='submit'
            isTransparent={isLoading}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            testId='save-button'
            buttonContainerClassName={cx('saveButton')}
          >
            {t('save')}
          </ButtonV2>
        </ButtonGroup>
      </div>
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('payee_reconciliation_setting_when_receive_invoice')}
          renderContent={
            <FormRadioButton<ReconciliationSettingForm>
              options={statusOptions}
              control={methods.control}
              name='ap_payee_reconciliation_setting.status'
              inputId='ap-payee-reconciliation-setting-status'
              vertical
            />
          }
        />
      </div>
    </Form>
  );
});

Edit.displayName = 'ReconciliationSettingEdit';
